import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Entity } from 'src/app/core/models/entity.model';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MultilevelItem } from 'src/app/core/models/multilevel-selector.model';
import { User } from 'src/app/core/models/user.model';
import * as utils from 'src/app/shared/helpers/utils';
import { forkJoin, map, Subscription } from 'rxjs';
import { LicenseService } from 'src/app/core/services/license.service';
import { Router } from '@angular/router';
import { PackageType } from 'src/app/core/models/package-type.model';
import { Package } from 'src/app/core/models/package.model';
import { SettingsService } from 'src/app/core/services/settings.service';
import { TranslateService } from '@ngx-translate/core';
import { EntityService } from 'src/app/core/services/entity.service';
import { ConfigService } from 'src/app/core/services/config.service';
import { Groups } from 'src/app/core/models/group.model';
import { hasDeadStandalonePackage, isStandalonePackageType } from 'src/app/shared/helpers/standalone-package.helper';

// The limit is one live package of the no-device GROUP per optical, not one 360 Depict.
// What counts as standalone lives in the helper above, shared with no-device-reassign-modal.
const PACKAGE_STATUS_TEST = 2;
const PACKAGE_STATUS_ACTIVE = 3;
const PACKAGE_STATUS_INACTIVE = 5;
// A package parked at a base entity is only handed down while its licenses are still running.
// Anything else sitting there is expired, pending deactivation or mid-upgrade, and offering it
// would let a dead package reach an optical shop: the backend does not check the status either.
const STATUSES_ASSIGNABLE_FROM_BASE = [PACKAGE_STATUS_TEST, PACKAGE_STATUS_ACTIVE];
import { TreeNode } from 'src/app/shared/components/basic-virtual-scroll/basic-virtual-scroll.component';


@Component({
  selector: 'hbo-reassign-modal',
  templateUrl: './reassign-modal.component.html',
  styleUrls: ['./reassign-modal.component.scss']
})
export class ReassignModalComponent implements OnChanges {

  @Input() entity!: Entity;
  @Input() canceledUpgradeId?: number | null;
  canceledUpgradeSameType: boolean = false;
  canceledPackage!: Package;
  entityToSettings!: Entity;
  @Input() action!: string;
  @Input() package!: Package;
  // Package already chosen by the caller: only the destination entity is left to pick.
  // Kept apart from `package` because that one gates the availability search on status 2/3.
  @Input() presetPackage?: Package | null;
  presetEntities: Entity[] = [];
  @Input() package_types_filter!: PackageType[];
  selectedType: string | null = null;
  availablePackages: number = 0;
  availablePackages$!: Package[] | null;
  finalAvailablePackages$!: Package[] | null;
  availablePackageHeadOffice!: Package[] | null;
  availablePackageDist!: Package[] | null;
  takeHeadOfficeAvalilable: Boolean = false;
  statusReassignModal: Boolean = false;

  @Input() id?: string | number;
  @Output() confirm = new EventEmitter();
  @Output() modalsCrossData = new EventEmitter();
  // Fired once the modal is really gone (not on the same-group warning bounce), so the host can drop
  // whatever it handed over as presetPackage.
  @Output() closed = new EventEmitter();

  typeData: string = 'reassignPackage';

  type: FormControl<number | null> = new FormControl(null, Validators.required);
  user: User = utils?.localStorageGetItem('user');
  entityId: FormControl<number | null> = new FormControl(null, this.action === 'reassign' ? Validators.required : null);

  reassignForm = this.formBuilder.nonNullable.group({
    entityId: this.entityId,
    type: this.type,
    entities: this.user.roleId !== 1 ? `[${this.user.entity.id}]` : null,
    perPage: -1,
    status: 1
  });

  reassignFormTypeLstnr: Subscription = new Subscription;

  reassignFormEntityLstnr: Subscription = new Subscription;

  selectorEntitiesSearch!: string;

  maxLevel!: number;
  minLevel!: number;
  data: MultilevelItem[] = [];
  parentEntities: Entity[] = [];

  confirmDisabled: boolean = true;

  headoffice!: Entity | null;
  dist!: Entity;

  packageTypes: PackageType[] = [];
  allPackageTypes: PackageType[] = [];
  selectedPackageType!: PackageType;

  haveLicencesSameGroup: boolean = false;
  licenseInactiveData!: string;
  entityOperativePackages$!: Package[] | null;

  packageSelectedId!: number | null;
  testDays!: number;
  originTestDays!: number;

  reassignArray: number[] = [];

  assignedToEntityYpeId!: number;

  alignTestDaysMessage: boolean = true;

  group!: string;
  groups: Groups = new Groups;

  // Backend rejection shown inside the modal. The toast interceptor only says "update failed",
  // so the actual reason has to be surfaced here.
  reassignError: string | null = null;

  private static readonly ERROR_KEYS: Record<string, string> = {
    ENTITY_HAS_ACTIVE_DEPICT_LICENSE: 'COMMON.REASSIGN.ERRORS.ENTITY_HAS_ACTIVE_DEPICT_LICENSE',
  };

  @ViewChild('dismissButton') dismissButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('modalRoot') modalRoot?: ElementRef<HTMLElement>;

  // Bumped on open so the entity tree rebuilds for whichever instance is being shown: the hierarchy
  // service keeps the built list in one shared slot, so the visible modal has to own it.
  treeReloadToken = 0;
  private pristinePackageTypes: PackageType[] | null = null;
  // The same-group warning is acknowledged by the host reopening this modal, and that bounce reaches
  // the listeners below as a plain shown/hidden pair. Taken for a fresh open it wipes the very
  // selection the user is about to confirm, so the assignment becomes impossible.
  private bouncingForWarning = false;
  // Picking a destination re-runs the availability query more than once (the entityId patch, then the
  // `entities` setValue inside its own listener), and the preset flow raises the warning from that
  // response. Clicking the toggle again on an already open warning closes it instead of reopening it,
  // taking the stacked backdrop with it, so the raise has to happen once per destination.
  private sameGroupWarningRaised = false;
  private onShown = () => {
    // A bounce comes back to the same instance, tree included, so nothing may be rebuilt: reloading it
    // clears the tree's own selection and header, and the destination vanishes from the UI while the
    // form still holds it.
    if (this.bouncingForWarning) {
      this.bouncingForWarning = false;
      return;
    }
    this.resetModalState();
    this.treeReloadToken++;
  };
  private onHidden = () => {
    if (this.bouncingForWarning) return;
    this.resetModalState();
    // resetModalState only reaches what this modal owns; presetPackage belongs to the host. Letting it
    // know lets it drop the package, which takes customTreeNode back to undefined: the tree then
    // rebuilds from the full list and republishes it, so the narrowed scope stops leaking into every
    // other selector's search.
    this.closed.emit();
  };

  // Everything the modal accumulates lives only while it is open
  private resetModalState(): void {
    if (this.pristinePackageTypes) {
      this.package_types_filter = this.pristinePackageTypes;
      this.packageTypes = this.pristinePackageTypes;
    }
    this.resetSelection();
    this.reassignError = null;
    this.haveLicencesSameGroup = false;
    this.sameGroupWarningRaised = false;
    this.policyPackageTypeIds = null;
    this.entityOperativePackages$ = null;
    this.reassignArray = [];
    this.selectedType = null;
    this.reassignForm.controls.entityId.patchValue(null, { emitEvent: false });
    this.reassignForm.controls.type.patchValue(
      this.presetPackage ? this.presetPackage.packageTypeId : null, { emitEvent: false });
  }

  // Closing must go through a dismiss button inside the modal so bootstrap runs its own teardown.
  // The host navigates right after, destroying the element mid-transition, and bootstrap appends
  // the backdrop to <body>: left alone it survives the navigation and locks the screen.
  private closeModal(): void {
    this.dismissButton?.nativeElement.click();
    setTimeout(() => {
      if (document.querySelector('.modal.show')) return; // another modal is legitimately open
      document.querySelectorAll('.modal-backdrop').forEach(backdrop => backdrop.remove());
      document.body.classList.remove('modal-open');
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');
    }, 300);
  }

  // Package type ids the destination's policy allows. null while unresolved, so nothing is blocked
  // before the answer arrives.
  policyPackageTypeIds: number[] | null = null;

  // Same resolution package creation uses: the target entity's own team drives the policy, and when
  // it has none the call sends no teamId and the distributor policy applies as the fallback.
  resolveEntityTeamId = (entityId: number): number | undefined => {
    const targetEntity: Entity | undefined =
      this.user.childEntities?.find((child: Entity) => child.id === entityId)
      ?? (this.user.entity?.id === entityId ? this.user.entity : undefined);
    return targetEntity?.teamId ?? undefined;
  }

  // States where the destination itself makes the assignment impossible, whichever package is picked:
  // offering a chooser there only invites hunting for one that works.
  get assignmentBlocked(): boolean {
    return this.depictConflict || this.policyBlocked || this.sameGroupBlocked;
  }

  get sameGroupBlocked(): boolean {
    return this.user.roleId !== 1
      && this.haveLicencesSameGroup
      && !this.skipsSameGroupWarning;
  }

  // The soft warning used to be skipped for every no-device package, because its wording is about
  // devices. But the protection it carries is not about devices: it stops a shop being handed a
  // fresh trial period on top of a package it already had. That applies to a no-device package
  // just the same, so it joins the warning when the destination holds a DEAD one — a live one is
  // already a hard rejection (depictConflict) and warning about it would only be noise.
  // Fit Depict carries a device, so it never took this path and is unaffected.
  get skipsSameGroupWarning(): boolean {
    if (!this.isStandaloneType(Number(this.reassignForm.controls.type.value))) return false;
    return !this.hasDeadStandalone;
  }

  private get hasDeadStandalone(): boolean {
    return hasDeadStandalonePackage(this.allPackageTypes, this.entityOperativePackages$, this.packageSelectedId);
  }

  // The admin wording asks them to confirm the shop needs more than one device, which means
  // nothing for a no-device package: that flow gets its own text.
  get sameGroupWarningIsNoDevice(): boolean {
    return this.isStandaloneType(Number(this.reassignForm.controls.type.value));
  }

  get policyBlocked(): boolean {
    if (!this.policyPackageTypeIds) return false;
    const typeId = Number(this.reassignForm.controls.type.value);
    return !!typeId && !this.policyPackageTypeIds.includes(typeId);
  }

  private resolveErrorMessage(err: unknown): string {
    const body = (err as { error?: { code?: string; error?: string; message?: string } })?.error;
    const key = body?.code ? ReassignModalComponent.ERROR_KEYS[body.code] : undefined;

    if (key) return this.translateService.instant(key);

    return body?.error
      || body?.message
      || this.translateService.instant('COMMON.REASSIGN.ERRORS.GENERIC');
  }

  constructor(
    public router: Router,
    private formBuilder: FormBuilder,
    private licenseService: LicenseService,
    private settingsService: SettingsService,
    private translateService: TranslateService,
    private entityService: EntityService,
    public configSvc: ConfigService
  ) {
    this.user.childEntities = this.configSvc.currentConfigValue.childEntities;
    this.availablePackages = 0;
    this.reassignForm.controls["type"].setValidators([Validators.required]);
  }

  ngOnChanges(changes: SimpleChanges) {
    // Keep the untouched list: filterPackageTypesByPolicy narrows package_types_filter for the chosen
    // destination, and without restoring it the next package's type is missing from the options, so
    // the select falls back to printing its raw id.
    if (changes['package_types_filter'] && this.package_types_filter) {
      this.pristinePackageTypes = this.package_types_filter;
      // Modify picks no destination entity, so the availability response that used to trigger the
      // narrowing never arrives.
      if (this.action === 'modify') this.narrowTypesToPackageGroup();
    }
    if (!changes['presetPackage']) return;
    this.presetEntities = this.presetPackage ? this.buildPresetEntities(this.presetPackage) : [];
    // Prefill the type from the caller's package so only the destination entity is left open.
    // Silent patch: the availability lookup is useless until a destination is picked, and it
    // runs anyway on the entity change, which is what selects the package and flags same-group licenses.
    if (this.presetPackage) {
      this.resetSelection();
      this.reassignForm.controls.type.patchValue(this.presetPackage.packageTypeId, { emitEvent: false });
    }
  }

  // A modification never leaves the package's own group. Read off the type, not off a single app:
  // "Fit Depict" bundles the no-device 360Depict app next to Fit and is still Invision.
  private narrowTypesToPackageGroup(): void {
    const group = this.packageGroupOf(this.package?.packageTypeId);
    // Left untouched on a race: the caller already narrowed this list.
    if (!group) return;
    this.package_types_filter = (this.package_types_filter ?? []).filter((item: PackageType) =>
      item.id !== this.package.packageTypeId && this.packageGroupOf(item.id) === group);
  }

  // The packageType nested inside a package carries no packageGroup, so always use the catalogue.
  private packageGroupOf(packageTypeId?: number | null): string | null | undefined {
    const catalogue: PackageType[] = this.configSvc.currentConfigValue.packageTypes ?? this.allPackageTypes ?? [];
    return catalogue.find((packageType: PackageType) => packageType.id === Number(packageTypeId))?.packageGroup;
  }

  // Destinations for the preset flow: everything hanging below the entity holding the package.
  // No parent chain to walk here, the package already sits at the root of the scope.
  private buildPresetEntities(_package: Package): Entity[] {
    const allEntities: Entity[] = this.configSvc.currentConfigValue.childEntities || [];
    const entityMap = new Map<number, Entity>(allEntities.map((e: Entity) => [e.id, e]));
    const rootId = _package.assignedEntityId;

    // Match on the parent link so the root itself never needs to be in the map:
    // a head office does not get its own entity back in childEntities.
    const isUnder = (e: Entity): boolean => {
      let node: Entity | undefined = e;
      while (node) {
        if (node.parentEntityId === rootId) return true;
        if (!node.parentEntityId) break;
        node = entityMap.get(node.parentEntityId);
      }
      return false;
    };

    return allEntities.filter((e: Entity) => !e.isBase && isUnder(e));
  }

  ngOnInit() {
    if (this.canceledUpgradeId) {
      this.licenseService.showPackage(String(this.canceledUpgradeId)).subscribe(data => this.canceledPackage = data);
    }
    if (this.action === 'modify') {
      if (this.user.roleId <= 2 && this.package.id) {
        this.getAncestors(this.package.assignedEntityId);
        this.headoffice
          ? this.reassignForm.get('entities')?.setValue(`[${this.dist!.id},${this.package.ownerEntityId}]`)
          : this.reassignForm.get('entities')?.setValue(`[${this.package.ownerEntityId}]`)
      } else {
        this.reassignForm.get('entities')?.setValue(`[${this.package.ownerEntityId}]`);
      }
    }
    this.reassignFormEntityLstnr = this.reassignForm.controls.entityId.valueChanges.subscribe(value => {

      //recover package types list from service or from config observable
      !this.configSvc.currentConfigValue.packageTypes
        ? this.licenseService.getPackageTypes().subscribe(packageTypes => {
          this.allPackageTypes = packageTypes.data;
          this.configSvc.setConfigParamsValue('packageTypes', this.allPackageTypes);
        })
        : this.allPackageTypes = this.configSvc.currentConfigValue.packageTypes;


      if (value) {
        this.resetSelection();
        // Re-arm the warning for the newly chosen destination: it is raised from the availability
        // response, which arrives more than once per pick.
        this.sameGroupWarningRaised = false;
        this.getAncestors(Number(this.reassignForm.controls.entityId.value));
        if (this.package) {
          this.headoffice
            ? this.reassignForm.get('entities')?.setValue(`[${this.dist!.id},${this.package.ownerEntityId}]`)
            : this.reassignForm.get('entities')?.setValue(`[${this.package.ownerEntityId}]`)
        } else {
          this.dist ? this.reassignForm.get('entities')?.setValue(`[${this.dist!.id}]`) : null;
        }

      }

    })

    this.reassignFormTypeLstnr = this.reassignForm.valueChanges.subscribe(data => {
      this.resetSelection();
      this.haveLicencesSameGroup = false;
      //get packages available
      // Only the destination's own packages are used here, to flag same-group licenses and a depict
      // conflict. The endpoint ignores entityId and filters by `entities`, so asking for the whole
      // distributor meant paging through thousands of rows and discarding nearly all of them.
      const destinationId = this.reassignForm.controls.entityId.value;
      let findAllPackagesFromEntity = { entities: destinationId ? `[${destinationId}]` : this.reassignForm.controls.entities.value, entityId: destinationId, perPage: this.reassignForm.controls.perPage.value };
      this.reassignForm.controls.type.valid && (!this.package || ([2, 3]).includes(this.package.status.id) && document.getElementById('reassignModal') && document.getElementById('reassignModal')!.className != 'modal fade')
        ?
        this.licenseService.availableAllPackages({ ...findAllPackagesFromEntity }).subscribe(res => {
          this.entityOperativePackages$ = res.data ? res.data.filter((_package: Package) => _package.assignedEntityId === this.reassignForm.controls.entityId.value && this.entityTypeId(_package.assignedEntityId!) === 4 && _package.category.id !== 2) : [];
          //TO DO//
          /*-----
          1 - an upgrader package finally get active
          2 - after, it become deactive, and keep originId ref, but it counts as a common deactive package
          ----*/
          //TO DO//
          if (data.type && data.entityId) this.detectSomeLicenseGroup()
          else this.haveLicencesSameGroup = false;
          // Preset flow: the package is already decided, so searching for candidates of its type and
          // offering a choice is pointless. Only its owner and assigned entity are about to change.
          if (this.presetPackage) {
            this.finalAvailablePackages$ = [this.presetPackage];
            this.availablePackages = 1;
            this.selectedType = String(this.presetPackage.packageTypeId);
            this.packageSelectedId = this.presetPackage.id;
            // The same-group warning is raised in the branch below, which this return skips: without
            // it a device package lands on a shop that already has one with nothing said. Same role
            // split as there — only role 1 gets the admin wording.
            if (this.action === 'reassign' && this.haveLicencesSameGroup && !this.sameGroupWarningRaised) {
              this.sameGroupWarningRaised = true;
              this.licenseInactiveData = `${this.user.childEntities!.find((child: Entity) => child.id === Number(this.reassignForm.controls.entityId.value))?.name}`;
              this.modalsCrossData.emit({
                licenseData: this.user.roleId <= 2 ? `${this.licenseInactiveData} - ${this.group}` : this.licenseInactiveData,
                noDevice: this.sameGroupWarningIsNoDevice
              });
              this.user.roleId === 1 ? this.adminInactiveLicenseDetected() : this.inactiveLicenseDetected();
            }
            this.letReassign();
            return;
          }
          forkJoin([
            this.licenseService.availableAllPackages({ ...this.reassignForm.value }),
            // Depict packages born at a base entity live there as TEST/ACTIVE, so the status filter
            // of the query above never returns them. Ask for them separately, by base entity.
            this.licenseService.availableAllPackages({ ...this.reassignForm.value, status: null, baseEntity: true })
          ]).pipe(
            // The base-entity query has to ask for every status (its packages are TEST/ACTIVE, not
            // the ASSIGNED the form filters by), so narrow it here instead. This also keeps both
            // result sets disjoint by status, so the merge cannot yield the same package twice.
            map(([parked, atBase]) => ({
              data: [
                ...(parked.data ?? []),
                ...(atBase.data ?? []).filter((_package: Package) =>
                  STATUSES_ASSIGNABLE_FROM_BASE.includes(_package.packageStatusId))
              ]
            }))
          ).subscribe(async res => {
            this.selectedType = this.reassignForm.controls.type.value?.toString()!;
            //reassignable packages are allways from operational category
            //if admin create a demo package has to assigne it to a shop forced
            //pending about privacy
            //this.user.roleId === 1 || _package.ownerEntityId === this.user.entityId
            //let admin reassign any license to any entity. Let other roles reassign only those packages on its own
            //only for a type
            //this.entityOperativePackages$ = res.data ? res.data.filter((_package: Package) => _package.assignedEntityId === this.reassignForm.controls.entityId.value  && this.entityTypeId(_package.assignedEntityId!) === 4 && _package.category.id !== 2) : [];
            //this.entityOperativePackages$.map((_package: Package) => _package.licenses.map((_license: License) => { if (_license.licenseStatusId === 5) { this.haveLicencesSameGroup = true } }));
            //this.availablePackages$ = res.data ? res.data.filter((_package: Package) => ((this.user.roleId === 1 || (this.user.roleId !== 1 && _package.assignedEntityId === this.user.entityId))) && this.entityTypeId(_package.assignedEntityId!) !== 4 && _package.category.id !== 2 && _package.packagePeriodId == this.package.packagePeriodId) : [];
            // A package parked at a base entity can only be handed down to an optical shop,
            // so it is only offered when the chosen destination is one.
            const targetIsOptical = this.entityTypeId(Number(this.reassignForm.controls.entityId.value)) === 4;
            if (this.action == 'reassign') {
              this.availablePackages$ = res.data ? res.data.filter((_package: Package) => ((this.user.roleId === 1 || (this.user.roleId !== 1 /*&& _package.assignedEntityId === this.user.entityId*/))) && (this.entityTypeId(_package.assignedEntityId!) !== 4 || (targetIsOptical && _package.assigned?.isBase && this.groups.noDevice.includes(_package.packageTypeId))) && _package.category.id !== 2) : [];
            } else {
              this.availablePackages$ = res.data ? res.data.filter((_package: Package) => this.entityTypeId(_package.assignedEntityId!) !== 4 && _package.category.id !== 2 && _package.packagePeriodId === this.package.packagePeriodId) : [];
            }
            if (this.user.roleId <= 2) {
              if (this.headoffice) {
                this.finalAvailablePackages$ = this.availablePackages$.filter(pack => (this.effectiveOwner(pack).id === this.headoffice?.id) || (this.effectiveOwner(pack).id !== this.headoffice?.id && this.effectiveOwner(pack).entityTypeId !== 3));
                this.availablePackageHeadOffice = this.finalAvailablePackages$.filter(pack => this.effectiveOwner(pack).entityTypeId === 3);
                if (this.availablePackageHeadOffice.length) this.takeHeadOfficeAvalilable = true;
                this.availablePackageDist = this.finalAvailablePackages$.filter(pack => this.effectiveOwner(pack).entityTypeId !== 3);
              } else {
                this.finalAvailablePackages$ = this.availablePackages$.filter(pack => this.effectiveOwner(pack).entityTypeId !== 3);
              }
              /*if (this.action === 'reassign')
              else {
                this.finalAvailablePackages$ = this.availablePackages$;
              }*/
              if (this.user.roleId == 2) {
                this.licenseInactiveData = `${this.user.childEntities!.find((child: Entity) => child.id === Number(this.reassignForm.controls.entityId.value))?.name}`;
                this.modalsCrossData.emit({ licenseData: `${this.licenseInactiveData} - ${this.group}`, noDevice: this.sameGroupWarningIsNoDevice });
                if (this.action === 'reassign') this.inactiveLicenseDetected();
              } else {//user role === 1
                this.licenseInactiveData = `${this.user.childEntities!.find((child: Entity) => child.id === Number(this.reassignForm.controls.entityId.value))?.name}`;
                this.modalsCrossData.emit({ licenseData: `${this.licenseInactiveData} - ${this.group}`, noDevice: this.sameGroupWarningIsNoDevice });
                if (this.action === 'reassign') this.adminInactiveLicenseDetected();
              }
            } else {
              //this.licenseInactiveData = `${this.user.childEntities.find((child: Entity) => child.id === Number(this.reassignForm.controls.entityId.value))?.name}: ${this.getTypeName(this.selectedType!)}`;
              this.licenseInactiveData = `${this.user.childEntities!.find((child: Entity) => child.id === Number(this.reassignForm.controls.entityId.value))?.name}`;
              this.modalsCrossData.emit({ licenseData: this.licenseInactiveData, noDevice: this.sameGroupWarningIsNoDevice });
              if (this.action === 'reassign') this.inactiveLicenseDetected();
              this.finalAvailablePackages$ = this.availablePackages$;
            }
            // Preset flow selects the caller's package instead of the first available one
            const preset = this.presetPackage
              ? this.finalAvailablePackages$?.find((pack: Package) => pack.id === this.presetPackage!.id)
              : null;
            if (preset) {
              this.checkClicked(preset);
            } else if (!this.headoffice) {
              //this.checkClicked(this.finalAvailablePackages$[0]);
              this.finalAvailablePackages$?.length ? this.checkClicked(this.finalAvailablePackages$![0]) : null;
            } else if (this.headoffice) {
              if (this.availablePackageHeadOffice?.length) {
                this.checkClicked(this.availablePackageHeadOffice![0]);
              } else {
                this.finalAvailablePackages$?.length ? this.checkClicked(this.finalAvailablePackages$![0]) : null;
              }
            }
            switch (this.action) {
              case 'modify':
                //select only packages inside the same group
                //this.finalAvailablePackages$ = this.finalAvailablePackages$.filter((pack:Package)=> pack.pa)
                this.narrowTypesToPackageGroup();
                break;
              default:
                break;
            }
            this.availablePackages = this.finalAvailablePackages$?.length;
            this.letReassign();
          })
        })
        : (
          this.availablePackages = 0,
          this.selectedType = null
        )
      this.letReassign();
    });

    this.maxLevel = Math.max(...this.user.childEntities!.map(o => o.entityTypeId));
    this.minLevel = this.user.entity.entityTypeId <= 4 ? this.user.entity.entityTypeId : Math.min(...this.user.childEntities!.map(o => o.entityTypeId));

    this.minLevel <= 4
      ? this.data.push({ visible: true, opened: true, all: true, selected: false, level: this.minLevel, type: this.minLevel, target: this.user.entity, childs: this.setLevel(this.user.entity) })
      : this.user.childEntities!
        .filter((item: Entity) => item.entityTypeId == this.minLevel)
        .map((item: Entity) => {
          this.data.push({ visible: true, opened: true, all: true, selected: false, level: this.minLevel, type: this.minLevel, target: item, childs: this.setLevel(item) });
        })
    this.reassignForm.patchValue({});
    this.action === 'modify' && this.package.packageTypeId
      ? (
        //this.filterPackageTypesByPolicy(this.package.ownerEntityId)
        this.reassignForm.controls.type.patchValue(this.package.packageTypeId),
        this.reassignForm.controls.type.patchValue(null)
      )
      : null;
  }
  ngOnDestroy() {
    this.reassignFormTypeLstnr.unsubscribe();
    this.reassignFormEntityLstnr.unsubscribe();
    const root = this.modalRoot?.nativeElement;
    root?.removeEventListener('shown.bs.modal', this.onShown);
    root?.removeEventListener('hidden.bs.modal', this.onHidden);
  }

  ngAfterViewInit() {
    this.setReassignTarget();
    const root = this.modalRoot?.nativeElement;
    if (!root) return;
    root.addEventListener('shown.bs.modal', this.onShown);
    root.addEventListener('hidden.bs.modal', this.onHidden);
  }

  // Each instance owns its warning modals: the ids are otherwise shared, and in the package details
  // `admin-some-license` already belongs to the package creation flow. Suffixed like this modal's own id.
  private get warningIdSuffix(): string {
    return this.id ? `-${this.id}` : '';
  }

  inactiveLicenseDetected() {
    if (this.skipsSameGroupWarning) return;
    if (this.haveLicencesSameGroup) {
      this.bouncingForWarning = true;
      let newButton = document.createElement('button');
      newButton.setAttribute('data-bs-toggle', 'modal');
      newButton.setAttribute('data-bs-target', `#confirmationModal-some-license-inactive${this.warningIdSuffix}`);
      document.body.appendChild(newButton);
      newButton.click();
      document.body.removeChild(newButton);
      // The preset flow's type is fixed and its select readonly, so nulling it only invalidates the
      // form: reassign() requires a valid one, and confirming would silently do nothing.
      if (!this.presetPackage) this.reassignForm.controls.type.patchValue(null);
    }
  }

  adminInactiveLicenseDetected() {
    if (this.skipsSameGroupWarning) return;
    if (this.haveLicencesSameGroup) {
      this.bouncingForWarning = true;
      let newButton = document.createElement('button');
      newButton.setAttribute('data-bs-toggle', 'modal');
      newButton.setAttribute('data-bs-target', `#confirmationModal-admin-some-license${this.warningIdSuffix}`);
      document.body.appendChild(newButton);
      newButton.click();
      document.body.removeChild(newButton);
    }
  }

  setReassignTarget() {
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutationRecord) {
        if (target?.className == 'modal fade') {
          form.controls.type.patchValue(null);
        }
      });
    });
    var target = document.getElementById('reassignModal');
    var form = this.reassignForm;
    if (observer instanceof Node) {
      observer.observe(target!, { attributes: true, attributeFilter: ['style'] });
    }

  }

  cancelSameTypeModal = () => {
    this.canceledUpgradeSameType = false;
    this.reassignForm.controls.type.patchValue(null);
  }

  resetSelection() {
    this.packageSelectedId = null;
    this.takeHeadOfficeAvalilable = false;
    this.availablePackages = 0;
    this.availablePackages$ = this.finalAvailablePackages$ = this.availablePackageHeadOffice = this.availablePackageDist = null;
  }
  setLevel(_item: Entity) {
    let levelChilds: MultilevelItem[] = [];
    this.user.childEntities!
      .filter((item: Entity) => item.parentEntityId == _item.id)
      .map((child: Entity) => {
        levelChilds.push({ visible: true, opened: true, all: true, selected: false, level: child.entityTypeId, type: child.entityTypeId, target: child, childs: this.setLevel(child) });
      });
    return levelChilds
  }

  callbackPartners(e: any) {
    console.log(e);
  }

  async reassign() {
    this.reassignForm.markAllAsTouched();
    this.reassignError = null;
    let finalPackage: Package;
    let entityMatch: Entity;
    let parentEntity: Entity;
    const patchedPackage: Package = new Package();
    if (this.reassignForm.valid) {
      switch (this.action) {
        case 'reassign':
          this.reassignForm.markAllAsTouched();
          if (this.packageSelectedId) {
            finalPackage = this.finalAvailablePackages$!.find((pack: Package) => pack.id === this.packageSelectedId)!;
          } else {
            if (this.takeHeadOfficeAvalilable) {
              finalPackage = this.availablePackageHeadOffice![0];
              delete this.availablePackageHeadOffice![0];
            } else if (this.availablePackageDist) {
              finalPackage = this.availablePackageDist[0];
              delete this.availablePackageDist[0];
            } else {
              finalPackage = this.finalAvailablePackages$![0];
              delete this.finalAvailablePackages$![0];
            }
          }
          entityMatch = this.user.childEntities!.find((child: Entity) => child.id == Number(this.reassignForm.controls.entityId.value))!;
          //parentEntity = this.user.childEntities.find((child: Entity) => child.id == Number(entityMatch.parentEntityId))!;
          finalPackage.ownerEntityId = entityMatch.parentEntityId;
          finalPackage.assignedEntityId = Number(this.reassignForm.controls.entityId.value);

          patchedPackage.id = this.packageSelectedId ? this.packageSelectedId : this.reassignArray[0];
          patchedPackage.assignedEntityId = finalPackage.assignedEntityId;
          // The plain update is rejected while the package still sits on an optical shop, and a base
          // entity is one. The no-device bypass covers it and resolves ownerEntityId server side.
          if (finalPackage.assigned?.isBase) {
            patchedPackage.reassignNoDevice = true;
          } else {
            patchedPackage.ownerEntityId = finalPackage.ownerEntityId;
          }

          this.iterateReassign(patchedPackage, finalPackage)//.finally(() => {

          // });

          break;
        case 'modify':
          this.reassignForm.markAllAsTouched();
          if (this.packageSelectedId) {
            finalPackage = this.finalAvailablePackages$!.find((pack: Package) => pack.id === this.packageSelectedId)!;
          } else {
            if (this.takeHeadOfficeAvalilable) {
              finalPackage = this.availablePackageHeadOffice![0];
            } else if (this.availablePackageDist) {
              finalPackage = this.availablePackageDist[0];
            } else {
              finalPackage = this.finalAvailablePackages$![0];
            }
          }

          entityMatch = this.user.childEntities!.find((child: Entity) => child.id == Number(this.package.assignedEntityId))!;
          parentEntity = this.user.childEntities!.find((child: Entity) => child.id == Number(entityMatch.parentEntityId))!;
          parentEntity
            ? (
              finalPackage.ownerEntityId = parentEntity.id,
              finalPackage.assignedEntityId = Number(this.package.assignedEntityId)
            )
            : finalPackage.assignedEntityId = Number(this.package.assignedEntityId)

          patchedPackage.id = this.package.id;
          patchedPackage.packageTypeId = finalPackage.packageTypeId;
          patchedPackage.upgrade = finalPackage.id;
          //modify allways modify a package assigned to a shop
          this.licenseService.updatePackage(patchedPackage).subscribe(res => {
            if (this.groups.mimesys.includes(finalPackage.packageType.id)) {
              this.entityToSettings = this.user.childEntities!.find((entity: Entity) => entity.id === finalPackage.assignedEntityId)!;
              if (this.entityToSettings && this.entityToSettings.entityTypeId === 4) {
                //this.settingsService.getMimesys(this.entityToSettings.id, this.entityToSettings.parentEntityId, this.entityToSettings.email, this.translateService.currentLang, this.entityToSettings.timezone, true)
                this.setExternalSettings(finalPackage.packageType.id, finalPackage.assignedEntityId);
              }
            } else if ([5, 6, 8].includes(finalPackage.packageType.id)) {
              this.setExternalSettings(5, finalPackage.assignedEntityId);
            } else if ([7].includes(finalPackage.packageType.id)) {
              this.setExternalSettings(7, finalPackage.assignedEntityId);
            }
            this.closeModal();
            this.router.navigate(['/licenses/packages/edit/' + finalPackage.id]);
          }, err => {
            this.reassignError = this.resolveErrorMessage(err);
          });
          break;
        default:
          break;
      }
    }
    this.confirm.emit();
  }
  onChange(id: number, _type?: string) {
    switch (_type) {
      case 'entity':
        //this.filterPackageTypesByPolicy(id);
        // The tree emits the string 'change' when a selection is cleared, never an id. Package
        // creation already guards against it the same way.
        if (typeof id !== 'number') break;
        this.reassignForm.controls.entityId.patchValue(id)
        // Every role needs this, not just admin: the destination's policy decides whether the
        // package type is allowed there at all.
        this.filterPackageTypesByPolicy(id);
        break;
      case 'type':
        if (this.user.roleId !== 1 && this.canceledPackage && id === this.canceledPackage.packageTypeId) {
          this.canceledUpgradeSameType = true;
        } else {
          this.canceledUpgradeSameType = false;
        }
        break;
      default:
        break;
    }
  }

  async setExternalSettings(_app: number, _id?: number) {
    // Set invision and thickness settings. If there is no invision Settings, there can't be thickness settings.
    // If there are invision settings we still have to check for the thickness ones, because we can have invision with no thickness
    let SettingsAncestor = await this.setSettingsAncestor(_id);

    if ([5, 7].includes(_app)) {
      this.settingsService.getInvision(this.entityToSettings.internalCode ? this.entityToSettings.internalCode : this.entityToSettings.id, SettingsAncestor.base64, this.entityToSettings.entityTypeId).subscribe(
        res => {
          if (_app == 7) {
            this.settingsService.getThickness(this.entityToSettings.internalCode ? this.entityToSettings.internalCode : this.entityToSettings.id, SettingsAncestor.base64, this.entityToSettings.entityTypeId).subscribe(
              res => { },
              err => {
                if (err.status == 404) {
                  this.settingsService.updateThickness(this.entityToSettings.internalCode ? this.entityToSettings.internalCode : this.entityToSettings.id, SettingsAncestor.base64, this.entityToSettings.entityTypeId, { "settings": null }).subscribe(
                    res => {
                      console.log('settings depict ok', res)
                    },
                    err => {
                      console.log('error', err);
                      //this.router.navigate(['/licenses/packages/edit/' + (_id ? _id : '')]);
                    });
                }
              })
          }
        },
        err => {
          if (err.status == 404) {
            this.settingsService.updateInvision(this.entityToSettings.internalCode ? this.entityToSettings.internalCode : this.entityToSettings.id, SettingsAncestor.base64, this.entityToSettings.entityTypeId, { "settings": { "timezone": this.entityToSettings.timezone } }).subscribe(
              res => {
                if (_app == 7) {
                  this.settingsService.updateThickness(this.entityToSettings.internalCode ? this.entityToSettings.internalCode : this.entityToSettings.id, SettingsAncestor.base64, this.entityToSettings.entityTypeId, { "settings": null }).subscribe(
                    res => {
                      console.log('settings depict ok', res)
                    },
                    err => {
                      console.log('error', err)
                      //this.router.navigate(['/licenses/packages/edit/' + (_id ? _id : '')]);
                    });
                }
              },
              err => {
                //this.router.navigate(['/licenses/packages/edit/' + (_id ? _id : '')]);
              });
          }
        }
      )
    }

    if (this.groups.mimesys.includes(_app)) {
      this.settingsService.getMimesys(this.entityToSettings.internalCode ? this.entityToSettings.internalCode : this.entityToSettings.id, SettingsAncestor.base64, this.entityToSettings.entityTypeId, "", "en-GB").subscribe(
        res => {
          console.log("Settings Mimesys exists");
        },
        err => {
          if (err.status == 404) {
            this.settingsService.updateMimesys(this.entityToSettings.internalCode ? this.entityToSettings.internalCode : this.entityToSettings.id, SettingsAncestor.base64, this.entityToSettings.entityTypeId, { "settings": { "timezone": this.entityToSettings.timezone } }).subscribe(
              res => {
                console.log("updating done");
              },
              err => {
                console.log("Error creating settings Mimesys", err);
                //this.router.navigate(['/licenses/packages/edit/' + (_id ? _id : '')]);
              });
          }
        }
      )
    }
  }

  setSettingsAncestor(_id?: number): Promise<{ data: any; base64: string }> {

    this.entityToSettings = this.user.childEntities!.find((entity: Entity) => entity.id === _id)!;

    return new Promise((resolve, reject) => {
      this.entityService.getAncestor(this.entityToSettings).subscribe(
        async res => {
          let parentEntity = res.parentEntity;
          let object = {
            "hierarchy": {
              "shop_id": String(this.entityToSettings.internalCode ? this.entityToSettings.internalCode : this.entityToSettings.id),
              "ancestor": {
                "shop_id": String(parentEntity?.internalCode ? parentEntity?.internalCode : parentEntity?.id),
                "ancestor": this.entityToSettings?.entityTypeId != 2
                  ? {
                    "shop_id": String(parentEntity?.parentEntity?.internalCode ? parentEntity?.parentEntity?.internalCode : parentEntity?.parentEntity?.id),
                    "ancestor": parentEntity?.parentEntity?.entityTypeId == 2
                      ? {
                        "shop_id": String(parentEntity?.parentEntity?.parentEntity?.internalCode ? parentEntity?.parentEntity?.parentEntity?.internalCode : parentEntity?.parentEntity?.parentEntity?.id),
                        "ancestor": null
                      }
                      : null
                  }
                  : null
              }
            }
          }
          resolve({ data: object, base64: utils.objectToBase64(object) });
        });
    });
  }

  getTypeName(_id: string) {
    let match = this.package_types_filter.find((item: PackageType) => item.id === Number(_id));
    if (match) return match.name
    else return ''
  }

  // Only optical shops can hold a no-device package, so nothing else is pickable once such a type
  // is selected. Declared as a property so its reference never changes: see disableNode in the BVS.
  disableNonOpticals = (node: TreeNode): boolean =>
    this.groups.noDevice.includes(Number(this.reassignForm.controls.type.value)) && node['entityTypeId'] !== 4;

  // A no-device package can only live on an optical shop, so those types stop being offered
  // as soon as the chosen destination is anything else. Mirrors disableNonOpticals.
  get selectableTypes(): PackageType[] {
    const types = this.package_types_filter ?? [];
    const targetId = Number(this.reassignForm.controls.entityId.value);
    const scoped = (!targetId || this.entityTypeId(targetId) === 4)
      ? types
      : types.filter((type: PackageType) => !this.groups.noDevice.includes(type.id));
    return this.presetPackage ? this.withPresetType(scoped) : scoped;
  }

  // The preset type is fixed and its select readonly, but the caller's list may not carry it: the
  // package details narrows it for the modify flow, which drops the package's own type on purpose.
  // Without a matching option ng-select prints the raw id instead of the name.
  private withPresetType(types: PackageType[]): PackageType[] {
    const presetTypeId = this.presetPackage!.packageTypeId;
    if (types.some((type: PackageType) => type.id === presetTypeId)) return types;
    const presetType = this.presetPackage!.packageType
      ?? this.allPackageTypes?.find((type: PackageType) => type.id === presetTypeId);
    return presetType ? [...types, presetType] : types;
  }

  private isStandaloneType(packageTypeId?: number): boolean {
    return isStandalonePackageType(this.allPackageTypes, packageTypeId);
  }

  // The backend refuses a no-device package landing on an optical shop that already holds a live
  // one. The confirm button dismisses the modal on click, so the rejection would arrive with
  // nowhere to be shown: detect it up front, from the destination's packages already loaded.
  // The limit is one live package OF THE NO-DEVICE GROUP, not one 360 Depict specifically.
  private isLiveDepict(_package: Package): boolean {
    // A Fit Depict holds the 360Depict licence bundled with Fit and does not occupy the slot.
    if (!this.isStandaloneType(_package?.packageTypeId)) return false;
    const fullyDead = _package.packageStatusId === PACKAGE_STATUS_INACTIVE
      && (!_package.renewalPersDate || new Date(_package.renewalPersDate) <= new Date());
    return !fullyDead;
  }

  get depictConflict(): boolean {
    if (this.action !== 'reassign') return false;
    // Keyed on the chosen type, not on the selected package: the same-group warning fires before
    // the package is auto-selected, and it has to know by then that this is a hard rejection.
    const typeId = Number(this.reassignForm.controls.type.value);
    // Only the standalone product conflicts. Assigning a Fit Depict to an optical that already
    // holds a 360 Depict is a legitimate sale, and the backend no longer rejects it either.
    if (!this.isStandaloneType(typeId)) return false;
    return (this.entityOperativePackages$ ?? []).some(
      (pack: Package) => pack.id !== this.packageSelectedId && this.isLiveDepict(pack)
    );
  }

  entityTypeId(_entityId: number) {
    return this.user.childEntities!.find((entity: Entity) => entity.id === _entityId)?.entityTypeId
  }

  // A package parked at a base entity belongs, for reassignment purposes, to whoever owns that base
  // entity, so it groups with that head office's or distributor's own packages.
  effectiveOwner(_package: Package): Entity {
    if (!_package.assigned?.isBase) return _package.assigned;
    const childEntities: Entity[] = this.user.childEntities || [];
    const base = childEntities.find((entity: Entity) => entity.id === _package.assignedEntityId);
    if (!base) return _package.assigned;
    const userEntity = this.configSvc.currentConfigValue.userEntity;
    return childEntities.find((entity: Entity) => entity.id === base.parentEntityId)
      ?? (userEntity?.id === base.parentEntityId ? userEntity : _package.assigned);
  }

  letReassign() {
    switch (this.action) {
      case 'reassign':
        /*this.availablePackages && this.reassignForm.valid
          ? this.confirmDisabled = false
          : this.confirmDisabled = true;*/
        break;
      case 'modify':
        this.availablePackages && this.reassignForm.valid && this.reassignForm.controls.type.value !== this.package.packageTypeId
          ? this.confirmDisabled = false
          : this.confirmDisabled = true;
        break;
      default:
        break;
    }

  }

  filterPackageTypesByPolicy(entityId: number) {
    if (entityId != 1) {
      let policyEntityId: number = 0;
      let entityMatch: Entity;
      let parentEntityMatch: Entity;
      if (this.user.entity.entityTypeId === 2 || this.user.entity.entityTypeId === 3) {
        entityMatch = this.user.entity
      } else {
        entityMatch = this.user.childEntities!.find((child: Entity) => child.id === entityId)!;
        switch (entityMatch?.entityTypeId) {
          case 4:
            parentEntityMatch = this.user.childEntities!.find((child: Entity) => child.id === entityMatch.parentEntityId)!;
            if (parentEntityMatch.entityTypeId === 3) entityMatch = parentEntityMatch;
            break;
          default:
            break;
        }
      }
      if (entityMatch?.entityTypeId >= 3) {
        policyEntityId = entityMatch.parentEntityId
      } else {
        policyEntityId = entityMatch?.id
      }
      if (entityMatch) {
        let errorPolicy!: string;
        const teamId: number | undefined = this.resolveEntityTeamId(entityId);
        this.policyPackageTypeIds = null;
        this.licenseService.showPolicy(policyEntityId, teamId).subscribe({
          next: policy => {
            errorPolicy = policy.error?.error;
            if (errorPolicy) {
              switch (errorPolicy) {
                case 'Policy not found':
                  this.packageTypes = this.allPackageTypes.filter(packageType => !packageType.private);
                  this.package_types_filter = this.packageTypes;
                  break;
                default:
                  break;
              }
            } else {
              if (policy.packagePolicies) {
                this.packageTypes = this.allPackageTypes.filter(packageType => policy.packagePolicies.some(packagePolicy => packagePolicy.packageType.id === packageType.id));
              }
              this.package_types_filter = this.packageTypes
            }
            this.policyPackageTypeIds = this.packageTypes.map((packageType: PackageType) => packageType.id);
          },
          error: () => {
            this.packageTypes = this.allPackageTypes.filter(packageType => !packageType.private);
            this.policyPackageTypeIds = this.packageTypes.map((packageType: PackageType) => packageType.id);
          }
        });
      } else {
        this.availablePackageHeadOffice = null;
        this.availablePackageDist = null;
        this.finalAvailablePackages$ = null;
      }

    }
    this.reassignArray = [];
  }

  getAncestors(_assignedId: number) {
    //let headoffice: Entity | null = null;
    //let dist: Entity | null = null
    let entityMatch = this.user.childEntities!.find((child: Entity) => child.id === _assignedId)!;
    let parentEntityMatch: Entity;
    if (this.user.roleId == 1) {
      parentEntityMatch = this.user.childEntities!.find((child: Entity) => child.id === Number(entityMatch?.parentEntityId))!;
    } else {
      if (entityMatch?.entityTypeId === 3 || this.user.roleId == 3) {
        parentEntityMatch = this.user.entity!;
      } else {
        parentEntityMatch = this.user.childEntities!.find((child: Entity) => child.id === Number(entityMatch?.parentEntityId))!;
      }
    }
    /*this.user.roleId <= 2
      ? parentEntityMatch = this.user.childEntities!.find((child: Entity) => child.id === Number(entityMatch.parentEntityId))!
      : parentEntityMatch = this.user.entity!;*/
    if (parentEntityMatch?.entityTypeId === 3) {
      let parentEntityMatchDist: Entity;
      if (this.user.roleId == 1) {
        parentEntityMatchDist = this.user.childEntities!.find((child: Entity) => child.id === Number(parentEntityMatch.parentEntityId))!;
      } else {
        parentEntityMatchDist = this.user.entity;
      }
      if (this.user.roleId != 3) {
        this.headoffice = parentEntityMatch;
        this.dist = parentEntityMatchDist;
      } else {
        this.headoffice = null;
        this.dist = parentEntityMatch;
      }
    } else {
      this.headoffice = null;
      this.dist = parentEntityMatch;
    }
  }
  headofficeSelected(_headOffice: boolean) {
    this.takeHeadOfficeAvalilable = _headOffice;
    this.takeHeadOfficeAvalilable
      ? this.packageSelectedId = this.availablePackageHeadOffice![0].id
      : this.packageSelectedId = this.availablePackageDist![0].id
  }

  checkClicked(_package: Package) {
    switch (this.action) {
      case 'reassign':
        let entityMatch = this.user.childEntities!.find((child: Entity) => child.id === this.reassignForm.controls.entityId.value)!;
        if (entityMatch) {
          this.assignedToEntityYpeId = entityMatch.entityTypeId;
          if (entityMatch.entityTypeId === 3) {
            this.reassignArray.includes(_package?.id)
              ? this.reassignArray = this.reassignArray.filter((el: number) => el !== _package?.id)
              : this.reassignArray.push(_package?.id);
          } else {
            this.packageSelectedId = _package?.id;
            this.testDays = _package?.testDays!;
            this.originTestDays = this.package?.remainingTestDays!;
            // A package parked at a base entity carries entityTypeId 4 on `assigned` itself; resolve
            // through effectiveOwner so it still counts as its real head office/distributor owner.
            // Packages assigned directly fall through effectiveOwner untouched, keeping their own type.
            _package && this.effectiveOwner(_package).entityTypeId === 3 ? this.takeHeadOfficeAvalilable = true : this.takeHeadOfficeAvalilable = false;
          }
        } else {
          this.availablePackageHeadOffice = null;
          this.availablePackageDist = null;
          this.finalAvailablePackages$ = null;
          this.packageSelectedId = null;
          this.reassignArray = [];
        }
        break;
      default:
        this.checkEveryLicenseNew(_package?.id);
        this.packageSelectedId = _package?.id;
        this.testDays = _package?.testDays!;
        this.originTestDays = this.package?.remainingTestDays!;
        _package && this.effectiveOwner(_package).entityTypeId === 3 ? this.takeHeadOfficeAvalilable = true : this.takeHeadOfficeAvalilable = false;
        break;
    }
  }

  iterateReassign(_package: Package, _finalPackage: Package): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.licenseService.updatePackage(_package).subscribe(res => {
        this.reassignArray = this.reassignArray.filter((el: number) => el != _package.id);
        if (this.reassignArray.length) {
          _package.id = this.reassignArray[0];
          this.iterateReassign(_package, _finalPackage);
        } else {
          this.reassignForm.controls.type.patchValue(null);
          // Only a successful reassignment takes the modal away, and before the host navigates
          this.closeModal();
          if (this.groups.mimesys.includes(_finalPackage.packageType.id)) {
            //settings mimesys
            let entity: Entity = this.user.childEntities!.find(entity => entity.id == _package.assignedEntityId)!;
            if (entity && entity.entityTypeId === 4) {
              this.setExternalSettings(_finalPackage.packageType.id, _package.assignedEntityId);
            }
            this.confirm.emit({ packageId: _package.id });
            resolve(true)
          } else {
            /*this.licenseService.updatePackage(patchedPackage).subscribe(res => {
              //this.router.navigate(['/licenses/packages']);
              this.reassignForm.controls.type.patchValue(null);
              if ([5, 6, 8].includes(finalPackage.packageType.id)) {
                this.setExternalSettings('navigate', 5, res.id);
              } else if ([7].includes(finalPackage.packageType.id)) {
                this.setExternalSettings('navigate', 7, res.id);
              }
            });*/

            //this.router.navigate(['/licenses/packages']);
            if ([5, 6, 8].includes(_finalPackage.packageType.id)) {
              this.setExternalSettings(5, _package.assignedEntityId);
            } else if ([7].includes(_finalPackage.packageType.id)) {
              this.setExternalSettings(7, _package.assignedEntityId);
            }
            this.confirm.emit({ packageId: _package.id });
            resolve(true)
          }
        }
      }, err => {
        this.reassignError = this.resolveErrorMessage(err);
        this.reassignArray = [];
        resolve(false);
      });
    });

  }

  checkEveryLicenseNew(_id: null | number) {
    let appsOrigin: any[] = [];
    let appsDestiny: any[] = [];
    const packageTypeDestiny: PackageType = this.configSvc.currentConfigValue.packageTypes?.find(packageType => packageType.id === this.reassignForm.controls.type.value)!;
    packageTypeDestiny.appPackages.map(appPackage => appsDestiny.push(appPackage.appId));
    const packageTypeOrigin: PackageType = this.configSvc.currentConfigValue.packageTypes?.find(packageType => packageType.id === this.package.packageTypeId)!;
    packageTypeOrigin.appPackages.map(appPackage => appsOrigin.push(appPackage.appId));
    const commonReduce = appsOrigin.reduce((acc, item) => {
      if (appsDestiny.includes(item)) acc.push(item);
      return acc;
    }, []);
    if (!commonReduce.length) this.alignTestDaysMessage = false
    else this.alignTestDaysMessage = true;
  }

  detectSomeLicenseGroup = () => {
    //const inactivePackagesToCheck = this.entityOperativePackages$?.filter((_package: Package)=> _package.packageStatusId ===5 && !_package.packageOriginId && !_package.packageUpgradeId);
    //reduce to restrict if has some package from same group
    const inactivePackagesToCheck = this.entityOperativePackages$;
    this.group = this.allPackageTypes.find((pt: PackageType) => pt.id === this.reassignForm.controls.type.value)?.appPackages[0].app.group!;
    inactivePackagesToCheck?.map((p: Package) => {
      this.allPackageTypes.map((item: PackageType) => item.appPackages[0].app.group === this.group && p.packageTypeId === item.id ? this.haveLicencesSameGroup = true : null)
    })
  }

}
